import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { KnowledgeMap } from "../components/KnowledgeMap";
import { MasteryTimeline } from "../components/MasteryTimeline";
import { useSpeech } from "../hooks/useSpeech";
import { Network, ChevronLeft, Youtube, StickyNote, BookOpen, FileText, Share2, LayoutGrid, Loader2, TrendingUp, BookOpenText, RefreshCw, Volume2, Square, Loader2 as Spinner, Mic2 } from "lucide-react";

const LEVEL_LABEL = ["Unseen", "Exposed", "Familiar", "Understands", "Can apply", "Can teach"];
const SOURCE_ICON = { video: Youtube, note: StickyNote, turbolearn: BookOpen, paper: FileText };

function levelColor(level) {
  if (level <= 1) return "text-red-400 border-red-400/40 bg-red-400/10";
  if (level <= 2) return "text-amber-400 border-amber-400/40 bg-amber-400/10";
  if (level <= 3) return "text-[var(--aria-primary)] border-[var(--aria-primary)]/40 bg-[var(--aria-primary)]/10";
  return "text-emerald-400 border-emerald-400/40 bg-emerald-400/10";
}

function ConceptStory({ concept }) {
  const navigate = useNavigate();
  const [drilling, setDrilling] = useState(false);
  const drill = async () => {
    setDrilling(true);
    try { const { data } = await api.post("/interviews", { target: `Review: ${concept}`, topic_focus: [concept] }); navigate(`/app/interviews/${data.interview_id}`); }
    catch { toast.error("Could not start drill"); setDrilling(false); }
  };
  const [story, setStory] = useState(null);
  const [busy, setBusy] = useState(true);
  const [showTimeline, setShowTimeline] = useState(false);
  const { toggle, speaking, activeKey, loadingKey, stop } = useSpeech();
  useEffect(() => () => stop(), []); // eslint-disable-line
  const load = async (refresh = false) => {
    setBusy(true);
    try { const { data } = await api.get(`/knowledge/concepts/${encodeURIComponent(concept)}/story${refresh ? "?refresh=true" : ""}`); setStory(data); }
    catch { setStory(null); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [concept]); // eslint-disable-line
  const TYPE_LABEL = { exposure: "Seen in", interview: "Interview", quiz: "Quiz", explanation: "Explained", application: "Applied" };
  return (
    <div className="aria-card aria-card-accent p-5 mb-6" data-testid="concept-story">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs uppercase tracking-[0.2em] text-[var(--aria-primary)] font-semibold flex items-center gap-1.5"><BookOpenText size={13} /> How you learned this</h3>
        <div className="flex items-center gap-3">
          {story?.story && (
            <button onClick={() => toggle(`${concept}. ${story.story}`, "story")} data-testid="concept-story-listen"
              className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded border ${speaking && activeKey === "story" ? "border-[var(--aria-primary)] text-[var(--aria-primary)]" : "border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>
              {loadingKey === "story" ? <Spinner size={11} className="animate-spin" /> : speaking && activeKey === "story" ? <Square size={11} /> : <Volume2 size={11} />}
              {speaking && activeKey === "story" ? "Stop" : "Listen"}
            </button>
          )}
          <button onClick={() => load(true)} disabled={busy} data-testid="concept-story-refresh" className="text-xs text-[var(--aria-text-muted)] hover:text-[var(--aria-text)] inline-flex items-center gap-1 disabled:opacity-50"><RefreshCw size={11} className={busy ? "animate-spin" : ""} /> Retell</button>
        </div>
      </div>
      {busy && !story ? <div className="text-sm text-[var(--aria-text-muted)]">Reading your evidence…</div> : story ? (
        <>
          <p className="text-[15px] leading-relaxed" data-testid="concept-story-text">{story.story}</p>
          <div className="text-xs text-[var(--aria-text-muted)] mt-3 flex items-center gap-3 flex-wrap">
            <span>First seen {story.first_seen} · {story.events} evidence event{story.events === 1 ? "" : "s"} · now {LEVEL_LABEL[story.level]}</span>
            {story.level < 3 && (
              <button onClick={drill} disabled={drilling} data-testid="concept-story-drill" className="btn btn-primary px-3 py-1 text-xs inline-flex items-center gap-1.5 disabled:opacity-60">
                {drilling ? <Spinner size={11} className="animate-spin" /> : <Mic2 size={11} />} Drill now — still below Understands
              </button>
            )}
          </div>
          <button onClick={() => setShowTimeline(!showTimeline)} data-testid="concept-story-timeline-toggle" className="mt-3 text-xs text-[var(--aria-primary)] hover:underline">{showTimeline ? "Hide" : "Show"} evidence timeline</button>
          {showTimeline && (
            <ol className="mt-3 space-y-1.5 border-l border-[var(--aria-border)] pl-4" data-testid="concept-story-timeline">
              {story.timeline.map((t, i) => (
                <li key={`${t.date || t.created_at || ""}-${t.level ?? ""}-${i}`} className="text-xs relative">
                  <span className="absolute -left-[21px] top-1 w-2 h-2 rounded-full bg-[var(--aria-primary)]" />
                  <span className="font-mono text-[var(--aria-text-muted)]">{t.date}</span> · <span className={levelColor(t.level).split(" ")[0]}>{TYPE_LABEL[t.type] || t.type} → {LEVEL_LABEL[t.level]}</span>
                  {t.detail && <div className="text-[var(--aria-text-muted)] line-clamp-2">{t.detail}</div>}
                </li>
              ))}
            </ol>
          )}
        </>
      ) : <div className="text-sm text-[var(--aria-text-muted)]">No evidence recorded for this concept yet.</div>}
    </div>
  );
}

export default function Knowledge() {
  const [concepts, setConcepts] = useState([]);
  const [graph, setGraph] = useState({ nodes: [], edges: [] });
  const [view, setView] = useState(() => localStorage.getItem("aria_knowledge_view") || "map");
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = async () => {
    try {
      const [c, g] = await Promise.all([api.get("/knowledge/concepts"), api.get("/knowledge/graph")]);
      setConcepts(c.data); setGraph(g.data);
    } catch (err) { console.error("[ARIA]", err); }
  };
  useEffect(() => { load(); }, []);  

  const switchView = (v) => { setView(v); localStorage.setItem("aria_knowledge_view", v); };

  const open = async (c) => {
    setLoadingDetail(true);
    try { const { data } = await api.get(`/knowledge/concepts/${encodeURIComponent(c.concept)}`); setDetail(data); }
    catch (err) { console.error("[ARIA]", err); } finally { setLoadingDetail(false); }
  };

  if (detail) {
    const related = [...new Set(detail.edges.map(e => e.from_concept === detail.mastery.concept ? e.to_concept : e.from_concept))];
    return (
      <div className="h-screen overflow-y-auto p-8">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => setDetail(null)} data-testid="back-to-knowledge" className="flex items-center gap-1.5 text-sm text-[var(--aria-text-muted)] hover:text-[var(--aria-text)] mb-6">
            <ChevronLeft size={14} /> Back to Knowledge
          </button>
          <div className="flex items-center justify-between mb-2">
            <h1 className="font-display text-3xl">{detail.mastery.concept}</h1>
            <span className={`aria-pill ${levelColor(detail.mastery.level)}`}>{LEVEL_LABEL[detail.mastery.level]}</span>
          </div>
          <p className="text-sm text-[var(--aria-text-muted)] mb-6">{detail.mastery.evidence_count} evidence event(s) · last updated {new Date(detail.mastery.last_updated).toLocaleDateString()}</p>

          <ConceptStory concept={detail.mastery.concept} />

          {related.length > 0 && (
            <div className="aria-card p-5 mb-6">
              <h3 className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold mb-3">Related concepts</h3>
              <div className="flex flex-wrap gap-2">
                {related.map(r => <span key={r} className="aria-pill text-xs">{r}</span>)}
              </div>
            </div>
          )}

          <div className="aria-card p-5 mb-6">
            <h3 className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold mb-3">Sources</h3>
            <div className="space-y-2">
              {detail.sources.map((s, i) => {
                const Icon = SOURCE_ICON[s.source_type] || FileText;
                return (
                  <div key={`${s.source_type}-${s.source_id || s.title || i}`} className="flex items-start gap-2.5 text-sm" data-testid={`knowledge-source-${i}`}>
                    <Icon size={14} className="text-[var(--aria-primary)] mt-0.5" />
                    <div>
                      <div className="font-medium">{s.source_title}</div>
                      <div className="text-xs text-[var(--aria-text-muted)] line-clamp-2">{s.text?.slice(0, 160)}…</div>
                    </div>
                  </div>
                );
              })}
              {detail.sources.length === 0 && <div className="text-sm text-[var(--aria-text-muted)]">No connected material found for this concept.</div>}
            </div>
          </div>

          <div className="aria-card p-5">
            <h3 className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold mb-3">Evidence history</h3>
            <div className="space-y-2">
              {detail.evidence.map(e => (
                <div key={e.evidence_id} className="text-sm border-l-2 border-[var(--aria-border)] pl-3">
                  <span className="font-mono text-xs text-[var(--aria-primary)] mr-2 uppercase">{e.evidence_type}</span>
                  {e.detail}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1"><Network size={22} className="text-[var(--aria-primary)]" /><h1 className="font-display text-3xl">Knowledge</h1></div>
            <p className="text-sm text-[var(--aria-text-muted)]">Every concept you've touched, its mastery level, and how it connects. Drag nodes to explore.</p>
          </div>
          <div className="flex rounded-md border border-[var(--aria-border)] overflow-hidden" data-testid="knowledge-view-toggle">
            <button onClick={() => switchView("map")} data-testid="knowledge-view-map"
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${view === "map" ? "bg-[var(--aria-primary)] text-white" : "text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>
              <Share2 size={13} /> Map
            </button>
            <button onClick={() => switchView("timeline")} data-testid="knowledge-view-timeline"
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 border-l border-[var(--aria-border)] ${view === "timeline" ? "bg-[var(--aria-primary)] text-white" : "text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>
              <TrendingUp size={13} /> Timeline
            </button>
            <button onClick={() => switchView("cards")} data-testid="knowledge-view-cards"
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 border-l border-[var(--aria-border)] ${view === "cards" ? "bg-[var(--aria-primary)] text-white" : "text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>
              <LayoutGrid size={13} /> Cards
            </button>
          </div>
        </div>

        {loadingDetail && <div className="text-xs text-[var(--aria-text-muted)] mb-3 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Opening concept…</div>}

        {view === "map" && graph.nodes.length > 0 && (
          <KnowledgeMap nodes={graph.nodes} edges={graph.edges}
            onSelect={(id) => { const c = concepts.find(x => x.concept === id) || { concept: id }; open(c); }} />
        )}

        {view === "timeline" && (
          <MasteryTimeline onSelect={(id) => { const c = concepts.find(x => x.concept === id) || { concept: id }; open(c); }} />
        )}

        {view === "cards" && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {concepts.map(c => (
            <button key={c.concept} onClick={() => open(c)} data-testid={`concept-card-${c.concept}`}
              className="aria-card aria-magnetic p-5 text-left">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-display text-lg leading-tight">{c.concept}</h3>
              </div>
              <span className={`aria-pill text-xs ${levelColor(c.level)}`}>{LEVEL_LABEL[c.level]}</span>
              <div className="text-xs text-[var(--aria-text-muted)] mt-3">{c.sources?.length || 0} connected source(s)</div>
            </button>
          ))}
          {concepts.length === 0 && (
            <div className="col-span-full text-center py-16 text-[var(--aria-text-muted)]">
              No concepts tracked yet — connect a lecture, note, or paper to begin building your knowledge graph.
            </div>
          )}
        </div>
        )}
        {view === "map" && graph.nodes.length === 0 && (
          <div className="text-center py-16 text-[var(--aria-text-muted)]">
            No concepts tracked yet — connect a lecture, note, or paper to begin building your knowledge graph.
          </div>
        )}
      </div>
    </div>
  );
}
